import { Track } from "@/src/lib/tracking";
import { AuthApiError } from "./api";
import type { AuthProvider } from "./types";

/**
 * 登录链路结果埋点（OpenReplay）—— 与原生 `AuthService` 同名同值域，两端漏斗可合并看。
 *
 * 只在 [AuthOrchestrator] 与邮箱发码出口发：成功 / 失败各只有这一处，调用方（弹窗、登录页）
 * 不再自己报失败，否则同一次失败会被记两遍。
 */

/** 失败发生在哪一段。email 的 `/auth/login` 段叫 verify（用户感知是「验证码不对」），社交叫 api。 */
export const AUTH_SIGNIN_ERROR_STAGE = {
  PROVIDER: "provider",
  SEND_CODE: "send_code",
  VERIFY: "verify",
  API: "api",
} as const;
export type AuthSigninErrorStage =
  (typeof AUTH_SIGNIN_ERROR_STAGE)[keyof typeof AUTH_SIGNIN_ERROR_STAGE];

/** 失败原因 —— 有界小写枚举；具体码放 `error_code`。 */
export const AUTH_FAIL_REASON = {
  NETWORK: "network",
  TIMEOUT: "timeout",
  SERVER: "server",
  INVALID_CODE: "invalid_code",
  INVALID_EMAIL: "invalid_email",
  REJECTED: "rejected",
  SDK: "sdk",
  UNKNOWN: "unknown",
} as const;
export type AuthFailReason =
  (typeof AUTH_FAIL_REASON)[keyof typeof AUTH_FAIL_REASON];

export const AUTH_PROVIDER_RESULT = {
  SUCCESS: "success",
  CANCEL: "cancel",
  FAIL: "fail",
} as const;
export type AuthProviderResult =
  (typeof AUTH_PROVIDER_RESULT)[keyof typeof AUTH_PROVIDER_RESULT];

/** 后端信封码（与 `EmailLogin/emailLoginErrors` 的提示映射同一组码）。 */
const ENVELOPE_CODE_REASON: Record<number, AuthFailReason> = {
  106039: AUTH_FAIL_REASON.INVALID_CODE,
  106038: AUTH_FAIL_REASON.INVALID_EMAIL,
};

const FIREBASE_CODE_REASON: Record<string, AuthFailReason> = {
  "auth/network-request-failed": AUTH_FAIL_REASON.NETWORK,
  "auth/timeout": AUTH_FAIL_REASON.TIMEOUT,
};

/** 用户主动取消 —— 不是失败，只记 `auth_provider_result{result:cancel}`。 */
const CANCEL_FIREBASE_CODES = new Set([
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/user-cancelled",
]);

/** NativeBridgeAdapter.cancel() 的固定 message；壳回传的 error.code 含 CANCEL 也算取消。 */
const NATIVE_BRIDGE_CANCEL_MESSAGE = "Cancelled";
const NATIVE_BRIDGE_TIMEOUT_MESSAGE = "Native bridge timeout";
const HTTP_SERVER_ERROR_MIN = 500;
const HTTP_SERVER_ERROR_MAX = 599;
const HTTP_CLIENT_ERROR_MIN = 400;

const ENVELOPE_CODE_PATTERN = /"code"\s*:\s*(-?\d+)/;

export interface AuthFailure {
  reason: AuthFailReason;
  errorCode: string;
}

function errorCodeOf(error: unknown): string | undefined {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === "string" && code ? code : undefined;
}

export function isAuthCancelled(error: unknown): boolean {
  const code = errorCodeOf(error);
  if (code && (CANCEL_FIREBASE_CODES.has(code) || /cancel/i.test(code))) {
    return true;
  }
  return (
    error instanceof Error && error.message === NATIVE_BRIDGE_CANCEL_MESSAGE
  );
}

/** 文本里的信封码：`{"code":106039,...}` 或 `INDEX_ERROR:{"code":106039,...}`。 */
function envelopeCodeFromText(text: string): number | undefined {
  const match = text.match(ENVELOPE_CODE_PATTERN);
  if (!match) {
    return undefined;
  }
  const numeric = Number(match[1]);
  return Number.isNaN(numeric) ? undefined : numeric;
}

/** `INDEX_ERROR:{"code":106039,...}`（useSendAuthCode 的统一抛错格式）里的信封码。 */
function envelopeCodeFromMessage(error: unknown): number | undefined {
  return error instanceof Error
    ? envelopeCodeFromText(error.message)
    : undefined;
}

function reasonOfStatus(status: number): AuthFailReason {
  const known = ENVELOPE_CODE_REASON[status];
  if (known) {
    return known;
  }
  // AuthApiError 的 status 既可能是 HTTP 状态码，也可能是信封码（6 位业务码），只认 5xx 区间为服务端故障
  if (status >= HTTP_SERVER_ERROR_MIN && status <= HTTP_SERVER_ERROR_MAX) {
    return AUTH_FAIL_REASON.SERVER;
  }
  return status >= HTTP_CLIENT_ERROR_MIN
    ? AUTH_FAIL_REASON.REJECTED
    : AUTH_FAIL_REASON.UNKNOWN;
}

/**
 * 各浏览器 fetch 网络失败的 TypeError 文案：Chrome `Failed to fetch`、Safari `Load failed`、
 * Firefox `NetworkError when attempting to fetch resource.`。
 */
const FETCH_NETWORK_ERROR_MESSAGES = [
  "Failed to fetch",
  "Load failed",
  "NetworkError",
];

function isFetchNetworkError(error: unknown): error is TypeError {
  return (
    error instanceof TypeError &&
    FETCH_NETWORK_ERROR_MESSAGES.some((message) =>
      error.message.includes(message),
    )
  );
}

/** 把任意登录异常归成 { reason, errorCode }。 */
export function resolveAuthFailure(error: unknown): AuthFailure {
  if (error instanceof AuthApiError) {
    // HTTP 非 2xx 时 status 是 HTTP 状态码，真正的业务码在响应体信封里（错码：400 + {"code":106039}）
    const code = envelopeCodeFromText(error.body) ?? error.status;
    return {
      reason: reasonOfStatus(code),
      errorCode: String(code),
    };
  }
  const envelopeCode = envelopeCodeFromMessage(error);
  if (envelopeCode !== undefined) {
    return {
      reason: reasonOfStatus(envelopeCode),
      errorCode: String(envelopeCode),
    };
  }
  const firebaseCode = errorCodeOf(error);
  if (firebaseCode) {
    return {
      reason: FIREBASE_CODE_REASON[firebaseCode] ?? AUTH_FAIL_REASON.SDK,
      errorCode: firebaseCode,
    };
  }
  // fetch 断网 / DNS / CORS 抛 TypeError，但解析 JWT 等编程错误也是 TypeError —— 只认浏览器网络错误文案
  if (isFetchNetworkError(error)) {
    return { reason: AUTH_FAIL_REASON.NETWORK, errorCode: error.name };
  }
  if (error instanceof Error) {
    const reason =
      error.message === NATIVE_BRIDGE_TIMEOUT_MESSAGE
        ? AUTH_FAIL_REASON.TIMEOUT
        : AUTH_FAIL_REASON.UNKNOWN;
    return { reason, errorCode: error.name };
  }
  return { reason: AUTH_FAIL_REASON.UNKNOWN, errorCode: "UNKNOWN" };
}

export function trackAuthSigninError(
  method: AuthProvider,
  stage: AuthSigninErrorStage,
  error: unknown,
): void {
  const { reason, errorCode } = resolveAuthFailure(error);
  Track.event("auth_signin_error", {
    method,
    stage,
    reason,
    error_code: errorCode,
  });
}

/** 社交授权段（Firebase popup/redirect、App 原生桥）结束：拿到 / 取消 / 失败。 */
export function trackAuthProviderResult(
  method: AuthProvider,
  result: AuthProviderResult,
  error?: unknown,
): void {
  Track.event("auth_provider_result", {
    method,
    result,
    reason:
      result === AUTH_PROVIDER_RESULT.FAIL
        ? resolveAuthFailure(error).reason
        : undefined,
  });
}

/** 验证码提交方式：填满 6 位自动提交 / 手动点 Verify（与原生 `login_emailcode_submit` 同值）。 */
export type EmailCodeSubmitTrigger = "auto" | "button";

/**
 * 提交验证码 —— 打在自动提交与按钮的**共用出口**。只看按钮 label
 * `login_emaillogincodeverifystep_click` 会漏掉全部自动提交（填满即提交，用户根本不点）。
 */
export function trackLoginEmailcodeSubmit(
  trigger: EmailCodeSubmitTrigger,
): void {
  Track.event("login_emailcode_submit", { trigger });
}

/** 发码触发：首次 Continue / 验证码页重发。 */
export type EmailCodeSendTrigger = "send" | "resend";

export const EMAIL_CODE_SEND_RESULT = {
  SUCCESS: "success",
  FAIL: "fail",
} as const;
export type EmailCodeSendResult =
  (typeof EMAIL_CODE_SEND_RESULT)[keyof typeof EMAIL_CODE_SEND_RESULT];

/** 发码结果；失败同时记一条 `auth_signin_error{stage:send_code}`，与其它阶段在同一漏斗里。 */
export function trackLoginEmailcodeSendResult(
  trigger: EmailCodeSendTrigger,
  result: EmailCodeSendResult,
  error?: unknown,
): void {
  if (result === EMAIL_CODE_SEND_RESULT.SUCCESS) {
    Track.event("login_emailcode_send_result", { result, trigger });
    return;
  }
  Track.event("login_emailcode_send_result", {
    result: EMAIL_CODE_SEND_RESULT.FAIL,
    trigger,
    reason: resolveAuthFailure(error).reason,
  });
  trackAuthSigninError("email", AUTH_SIGNIN_ERROR_STAGE.SEND_CODE, error);
}
